import {
    Card,
    CardContent,
    Stack,
    Avatar,
    Typography,
    Box,
    useTheme,
    alpha,
    Grid,
} from '@mui/material';
import {
    Group as GroupIcon,
    CheckCircle as CheckCircleIcon,
    SupervisorAccount as SupervisorAccountIcon,
    Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { themeConstants } from '@/theme';
import { useI18nContext } from '@/i18n/i18n-react';

interface StatsCardsProps {
    totalUsers: number;
    activeUsers: number;
    admins: number;
    categories: number;
}

export const StatsCards = ({ totalUsers, activeUsers, admins, categories }: StatsCardsProps) => {
    const theme = useTheme();
    const { LL } = useI18nContext();

    const statItems = [
        {
            value: totalUsers,
            label: LL.statsCards.totalUsers(),
            icon: <GroupIcon />,
            color: 'primary.main',
            gradient: themeConstants.gradients.cardPrimary,
            border: themeConstants.borders.primary,
        },
        {
            value: activeUsers,
            label: LL.statsCards.activeUsers(),
            icon: <CheckCircleIcon />,
            color: 'success.main',
            gradient: themeConstants.gradients.cardSuccess,
            border: themeConstants.borders.success,
        },
        {
            value: admins,
            label: LL.statsCards.admins(),
            icon: <SupervisorAccountIcon />,
            color: 'warning.main',
            gradient: themeConstants.gradients.cardWarning,
            border: themeConstants.borders.warning,
        },
        {
            value: categories,
            label: LL.statsCards.categories(),
            icon: <ScheduleIcon />,
            color: 'info.main',
            gradient: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)} 0%, ${alpha(theme.palette.info.dark, 0.15)} 100%)`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
        },
    ];

    return (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            {statItems.map((item, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                    <Card
                        sx={{
                            background: item.gradient,
                            border: item.border,
                            transition: themeConstants.transitions.smooth,
                            '&:hover': {
                                boxShadow: themeConstants.effects.boxShadowHover,
                            },
                        }}
                    >
                        <CardContent>
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: item.color }}>{item.icon}</Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight={700}>
                                        {item.value}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {item.label}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};
